import { Color, getThemeForKey } from "@mcleod/core";
import { TableRow } from "./TableRow";

/**
 * Manages the various background colors that can be displayed based on a row's settings, the table's current state,
 * user settings, etc.  See the included 'get effective*' methods for details.
 */
export class TableRowBackgroundColors {
    private static readonly SELECTED_COLOR: Color = getThemeForKey("table.selectionBackground");
    evenOddColor: Color;
    providedRowColor: Color;
    providedExpandComponentColor: Color;
    private row: TableRow;

    constructor(row: TableRow) {
        this.row = row;
    }

    /**
     * A table row can display one of several background colors.  The decision tree is as follows:
     * 1) If the row is selected, it will display the selected row color (from the theme).
     * 2) Otherwise, if the row has specified its own background color, it will display that color (this is the provided
     *    color).
     * 3) Otherwise, if the user has specified even/odd row colors in their user settings, use those colors (this is the
     *    evenOddColor, which is updated by the table any time the table's displayed rows change).
     * 4) If none of the above are true, the row will not display a background color.
     */
    get effectiveRowColor(): Color {
        return this.getEffectiveColor(this.providedRowColor);
    }

    /**
     * A table row's expansion area can display one of several background colors.  The decision tree is as follows:
     * 1) If the row is selected, it will display the selected row color (from the theme).
     * 2) Otherwise, if the expansion component has specified its own background color, it will display that color (this
     *    is the provided color).
     * 3) Otherwise, if the user has specified even/odd row colors in their user settings, use those colors (this is the
     *    evenOddColor, which is updated by the table any time the table's displayed rows change).
     * 4) If none of the above are true, the row will not display a background color.
     */
    get effectiveExpandComponentColor(): Color {
        return this.getEffectiveColor(this.providedExpandComponentColor ?? this.providedRowColor);
    }

    private getEffectiveColor(providedColor: Color) {
        if (this.row.selected) {
            return TableRowBackgroundColors.SELECTED_COLOR;
        }
        if (providedColor != null) {
            return providedColor;
        }
        if (this.evenOddColor != null) {
            return this.evenOddColor;
        }
        return null;
    }
}
